<template>
  <SogPage :title="_title">
    <SogCard>
      <SogCardActions :is-toolbar="true">
        <SogCardActionButton
          v-show="_currentInsuranceId"
          mdi-icon="mdi-information"
          @click="onDetails"
        >
          {{ _navigationStore.currentInsuranceNumber }}
        </SogCardActionButton>
        <!--
        <div
          style="position:relative;display:inline-block;margin:0px 10px 0px 10px;align-self: flex-end"
        >
-->
        <SogBadge
          v-show="_countActivation>0"
          :value="_countActivation"
          badge-style="primary"
          badge-type="notification"
          overlap="true"
        >
          <SogCardActionButton @click="onActivate">
            Aktivieren
          </SogCardActionButton>
        </SogBadge>
        <SogBadge
          v-show="_printVisible"
          :value="_countPrint"
          badge-style="primary"
          badge-type="notification"
          overlap="true"
        >
          <SogCardActionButton
            mdi-icon="mdi-printer"
            @click="onPrint(true)"
          />
        </SogBadge>
        <SogBadge
          v-show="_printExportVisible"
          :value="_countPrintExport"
          badge-style="primary"
          badge-type="notification"
          overlap="true"
        >
          <SogCardActionButton
            mdi-icon="mdi-printer"
            @click="onPrintExport"
          />
        </SogBadge>
        <SogTextBox
          v-model="_listPage.number"
          placeholder="VSNR/eVB"
          :interval-modified="onNumberModified"
        />
        <div style="width: 80px">
          <SogDropDownList
            v-model="_hasVehicle"
            placeholder="Zugelassen"
            :data-source="_lstYesNo"
            :fields="_fields"
            popup-width="80px"
          />
        </div>
        <div style="width: 80px">
          <SogDropDownList
            v-model="_activated"
            placeholder="Aktiviert"
            :data-source="_lstYesNo"
            :fields="_fields"
            popup-width="80px"
          />
        </div>
        <SogCardActionButton
          :kind="SogButtonKind.Search"
          style="align-self: flex-end"
          @click="search"
        />
        <SogCardActionButton
          :kind="SogButtonKind.FilterRemove"
          @click="onClear"
        />

        <SogCardActionMoreButton
          :items="_menuItems"
          @select="onMoreSelect"
        />
        <SogCardActionButton
          :kind="SogButtonKind.Back"
          style="align-self: flex-end"
        />
      </SogCardActions>
      <SogCardContent>
        <InsuranceListComponent
          ref="_listComponent"
          :selected="onSelected"
          @selected="rowSelected"
        />
      </SogCardContent>
    </SogCard>
  </SogPage>
</template>

<script setup lang="ts">
import SogPage from "@/shared/ui/SogPage.vue";
import {computed, onMounted, reactive, ref, watch} from "vue";
import {onBeforeRouteUpdate, useRouter} from "vue-router";
import {useNavigationStore} from "@/modules/Navigation/store";
import SogCard from "@/shared/ui/SogCard.vue";
import SogCardContent from "@/shared/ui/SogCardContent.vue";
import InsuranceListComponent from "@/modules/Insurance/components/InsuranceListComponent.vue";
import SogCardActions from "@/shared/ui/SogCardActions.vue";
import SogCardActionButton from "@/shared/ui/SogCardActionButton.vue";
import Insurance from "@/shared/model/Insurance";
import {InsuranceType} from "@/shared/model/insuranceType";
import apiInsurance from "@/modules/Insurance/api";
import apiFile from "@/shared/services/apiFile";
import {useInsuranceStore} from "@/modules/Insurance/store";
import SogTextBox from "@/shared/ui/SogTextBox.vue";
import SogDropDownList from "@/shared/ui/SogDropDownList.vue";
import {SogButtonKind} from "@/shared/ui/types/SogButtonKind";
import SogBadge from "@/shared/ui/SogBadge.vue";
import PrintExportInsuranceDialogComponent
  from "@/modules/Insurance/components/PrintExportInsuranceDialogComponent.vue";
import {storeToRefs} from "pinia";
import apiOrder from "@/modules/Order/api";
import {useAccountStore} from "@/stores/accountStore";
import SogCardActionMoreButton, {IActionItem} from "@/shared/ui/SogCardActionMoreButton.vue";
import {mdiPrinter} from "@mdi/js";
import {IdType} from "@/shared/model/idType";

interface Props {
  id?: string
  type?: string
}

const props = withDefaults(defineProps<Props>(), {
    id: undefined, type: "all"
  }
)


const _navigationStore = useNavigationStore()
const _insuranceStore = useInsuranceStore();
const _router = useRouter();

const _selectedInsurances = reactive<Insurance[]>([])
const _countActivation = ref(0);
const _countPrint = ref(0);
const _countPrintExport = ref(0)
const _hasVehicle = ref("all")
const _activated = ref("all")
const _currentInsuranceId = ref<string | undefined>();

const _listComponent = ref<InstanceType<typeof InsuranceListComponent> | null>(null)
const _printExportInsuranceDialog = ref<InstanceType<typeof PrintExportInsuranceDialogComponent> | null>(null)

const _type = ref("all")
const _orderHeadId = ref<string | undefined>(undefined)
const _personId = ref<string | undefined>(undefined)

const {isRoot: _isRoot} = storeToRefs(useAccountStore())


const _lstYesNo = reactive([{id: "no", value: "Nein"}, {id: "yes", value: "Ja"}, {id: "all", value: "Alle"}]);
const _fields = {text: 'value', value: 'id'}
const {listPage: _listPage, evbActivationPage: _activationPage} = storeToRefs(_insuranceStore)

const _printExportVisible = computed(() => {
  return _countPrintExport.value > 0 && _isRoot;
})

const _printVisible = computed(() => {

  return _countPrint.value > 0;

})


const _menuItems: IActionItem[] = reactive([
  {
    text: 'ohne VN',
    id: "printWithout",
    svgIcon: mdiPrinter,
    visible: false
  },
])

watch(_printVisible, () => {
    _menuItems[0].visible = _printVisible.value
  }, {immediate: true}
)


const _titleAddition = ref<string | undefined>(undefined)
const _title = computed(() => {
  if (_titleAddition.value) {
    return "Versicherungskarten für " + _titleAddition.value;
  } else {
    return "Versicherungskarten";
  }
})

async function p_init() {
  _type.value = props.type ?? "all";
  _orderHeadId.value = undefined
  if (_type.value === "order") {
    if (!props.id) {
      _navigationStore.back();
      return;
    }
    _orderHeadId.value = props.id
    _titleAddition.value = _navigationStore.params
    if (!_titleAddition.value) {
      loadOrder().then()
    }
  }

  if (_type.value === "person" && _navigationStore.currentPersonId) {
    _personId.value = _navigationStore.currentPersonId
    _titleAddition.value = _navigationStore.currentPersonName
  }
  _hasVehicle.value = _listPage.value.hasVehicle;
  _activated.value = _listPage.value.activated;

  search()
}

onMounted(async () => {
  await p_init()
})

onBeforeRouteUpdate(async () => {
  await p_init()
});


async function loadOrder() {
  if (props.id) {
    const res = await apiOrder.orderLoad(props.id)
    if (res) {
      _titleAddition.value = res.orderHead.orderNumber
    }
  }
}

watch(_selectedInsurances, () => {
  /*
    _countPrint.value = _selectedInsurances.filter(t=>{return t.insuranceType!==InsuranceType.Zoll&& !t.vehicleRegistrationId}).length
    _countPrintExport.value = _selectedInsurances.filter(t=>{return t.insuranceType===InsuranceType.Zoll&& !t.vehicleRegistrationId}).length
  */
  _countPrint.value = _selectedInsurances.filter(t => {
    return t.insuranceType !== InsuranceType.Zoll
  }).length
  _countPrintExport.value = _selectedInsurances.filter(t => {
    return t.insuranceType === InsuranceType.Zoll
  }).length
  _countActivation.value = _selectedInsurances.filter(t => {
    return t.insuranceType !== InsuranceType.Zoll && !t.evb
  }).length
})

function onSelected(list: Insurance[]) {
  _selectedInsurances.length = 0;
  _selectedInsurances.push(...list)
}

async function onPrint(policyHolder: boolean) {
  const t_ids = _selectedInsurances.filter(t => {
//    return t.insuranceType !== InsuranceType.Zoll && !t.vehicleRegistrationId
    return t.insuranceType !== InsuranceType.Zoll
  }).map(t => t.id ?? "")
  if (t_ids.length === 0)
    return
  const t_id = await apiInsurance.initPdf(t_ids, policyHolder)
  if (t_id) {
    await apiFile.downloadFile(t_id)
  }
}

async function onMoreSelect(id: string) {
  if (id === "printWithout") {
    await onPrint(false)
  }
}


async function onPrintExport() {
  const array = _selectedInsurances.filter(t => {
//    return t.insuranceType === InsuranceType.Zoll && !t.vehicleRegistrationId
    return t.insuranceType === InsuranceType.Zoll
  })

  await _navigationStore.showPrintExportInsurance(array);
}


async function onActivate() {
  const t_ids = _selectedInsurances.filter(t => {
    return t.insuranceType !== InsuranceType.Zoll && !t.evb
  }).map(t => t.id ?? "")
  if (t_ids.length === 0)
    return
  _activationPage.value.ids.length = 0;
  _activationPage.value.ids.push(...t_ids)
  await _router.push("/insurance/evbActivation")
}

function search() {


  if (!_listComponent.value || !_listComponent.value.adaptor)
    return


  _listComponent.value?.adaptor.removePredicate("number");
  if (_listPage.value.number) {
    _listComponent.value?.adaptor.addPredicate("number", "contains", _listPage.value.number.toUpperCase());
  }

  _listComponent.value?.adaptor.removePredicate("vehicleRegistration");
  if (_hasVehicle.value === "yes") {
    _listComponent.value?.adaptor.addPredicate("vehicleRegistration", "equal", "yes");
  }
  if (_hasVehicle.value === "no") {
    _listComponent.value?.adaptor.addPredicate("vehicleRegistration", "equal", "no");
  }

  _listComponent.value?.adaptor.removePredicate("activated");
  if (_activated.value === "yes") {
    _listComponent.value?.adaptor.addPredicate("activated", "equal", "yes");
  }
  if (_activated.value === "no") {
    _listComponent.value?.adaptor.addPredicate("activated", "equal", "no");
  }

  _listComponent.value?.adaptor.removePredicate("personid");
  if (_personId.value) {
    _listComponent.value?.adaptor.addPredicate("personid", "equal", _personId.value ?? "");
  }
  _listComponent.value?.adaptor.removePredicate("orderheadid");
  if (_orderHeadId.value) {
    _listComponent.value?.adaptor.addPredicate("orderheadid", "equal", _orderHeadId.value ?? "");
  }


  _listComponent.value.refresh()
  //vehicleRegistrationId
}

watch(_hasVehicle, (value) => {
    _listPage.value.hasVehicle = value
    if (value === "yes" && _activated.value === "no") {
      _activated.value = "all"
      return
    }
    search()
  }
)

watch(_activated, (value) => {
    _listPage.value.activated = value
    search()
  }
)

function onDetails() {
  if (_navigationStore.currentInsuranceId) {
    _navigationStore.showObject(IdType.Insurance, _navigationStore.currentInsuranceId)
  }
}

/*
watch(_number,()=>{
  if(_interval.value){
    clearInterval(_interval.value)
  }
  _key.value = getGuid();
  if(_number.value.length>0&&_number.value.length<3)
    return
  _interval.value = setInterval(async (key: string) => {
    if (_interval.value) {
      clearInterval(_interval.value)
    }
    if (key !== _key.value)
      return;
  },1000,_key.value)
})
*/

function onNumberModified() {
  search();
}

function rowSelected(data: any | undefined) {
  _currentInsuranceId.value = data?.id
}

function onClear() {
  _listPage.value.number = "";
  _listPage.value.hasVehicle= "all";
  _listPage.value.activated= "all";
}


</script>
<style scoped>
</style>
